import { useState, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Gift, X, AlertTriangle, Check } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format, addDays } from "date-fns";
import { cn } from "@/lib/utils";

interface SelectedKeepsake {
  id: string;
  name: string;
  image_url?: string | null;
}

interface GiftPersonalisationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactName?: string;
  contactId?: string;
  generatedMessage?: string;
  occasionType?: string;
  productId: string;
  productName?: string;
  productPrice?: string;
  productImageUrl?: string;
  productCategory?: string;
  selectedKeepsake?: SelectedKeepsake;
}

const MESSAGE_MAX = 300;
const INSCRIPTION_MAX = 10;

const HK_PHONE_RE = /^(\+852\s?\d{4}\s?\d{4}|\d{8})$/;
const INTL_PHONE_RE = /^\+?\d[\d\s\-()]{6,18}\d$/;

const HK_KEYWORDS = [
  "hong kong", "hk", "kowloon", "new territories",
  "tsim sha tsui", "mong kok", "wan chai", "causeway bay",
  "central", "sha tin", "tuen mun", "yuen long",
  "tai po", "sai kung", "tseung kwan o", "aberdeen",
  "ap lei chau", "stanley", "repulse bay",
  "lantau", "discovery bay", "tung chung",
];

function looksLikeHK(address: string): boolean {
  const lower = address.toLowerCase();
  return HK_KEYWORDS.some((kw) => lower.includes(kw));
}

function validatePhone(phone: string): string | null {
  const trimmed = phone.trim();
  if (!trimmed) return "Contact number is required.";
  if (HK_PHONE_RE.test(trimmed.replace(/\s/g, ""))) return null;
  if (INTL_PHONE_RE.test(trimmed)) return null;
  return "Please enter a valid phone number (e.g. +852 9123 4567).";
}

function validateAddress(address: string): string | null {
  const trimmed = address.trim();
  if (!trimmed) return "Delivery address is required.";
  if (trimmed.length < 10) return "Address seems too short — please include full details.";
  return null;
}

function isGreetingCard(category?: string): boolean {
  return category === "greeting_card";
}

/** Derive a short product key from the product name */
function getProductKey(name?: string): string {
  if (!name) return "other";
  const n = name.toLowerCase();
  if (n.includes("journal")) return "journal";
  if (n.includes("mug")) return "mug";
  if (n.includes("phone case")) return "phone_case";
  if (n.includes("key holder")) return "key_holder";
  if (n.includes("calendar")) return "desk_calendar";
  if (n.includes("signature")) return "signature_box";
  if (n.includes("greeting") || n.includes("card")) return "greeting_card";
  return "other";
}

/** For signature box, derive which keepsake-specific fields to show */
function getKeepsakeKey(keepsake?: SelectedKeepsake): string {
  if (!keepsake) return "other";
  const n = keepsake.name.toLowerCase();
  if (n.includes("journal")) return "journal";
  if (n.includes("mug")) return "mug";
  if (n.includes("phone case")) return "phone_case";
  if (n.includes("key holder")) return "key_holder";
  if (n.includes("calendar")) return "desk_calendar";
  return "other";
}

const WhatsAppNote = () => (
  <p className="text-xs text-muted-foreground">
    We'll ask for your photo via WhatsApp before production begins.
  </p>
);

const GiftPersonalisationModal = ({
  open,
  onOpenChange,
  contactName,
  contactId,
  generatedMessage = "",
  occasionType = "",
  productId,
  productName = "Dearestly Personalised Card",
  productPrice = "USD 12.00",
  productImageUrl,
  productCategory,
  selectedKeepsake,
}: GiftPersonalisationModalProps) => {
  const isMobile = useIsMobile();
  const reduce = useReducedMotion();
  const productKey = getProductKey(productName);
  const isCard = isGreetingCard(productCategory);

  // All products use 7 day minimum
  const minDays = 7;
  const earliestDate = addDays(new Date(), minDays);

  // --- state ---
  const [recipientName, setRecipientName] = useState(contactName ?? "");
  const [senderWhatsapp, setSenderWhatsapp] = useState("");
  const [message, setMessage] = useState(generatedMessage ?? "");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryContact, setDeliveryContact] = useState("");
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(undefined);
  const [deliveryEmail, setDeliveryEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [showDigitalDialog, setShowDigitalDialog] = useState(false);
  const [isDigitalOnly, setIsDigitalOnly] = useState(false);
  const [showNonHKBlock, setShowNonHKBlock] = useState(false);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);
  const [showGiftSuccess, setShowGiftSuccess] = useState(false);

  // product-specific fields
  const [personalisationName, setPersonalisationName] = useState("");
  const [phoneType, setPhoneType] = useState("");
  const [keyHolderMode, setKeyHolderMode] = useState<"photo" | "inscription">("photo");
  const [inscription, setInscription] = useState("");

  // Sync props when modal opens
  useEffect(() => {
    if (open) {
      setRecipientName(contactName ?? "");
      setMessage(generatedMessage ?? "");
      setPhoneError(null);
      setAddressError(null);
      setWhatsappError(null);
      setIsDigitalOnly(false);
      setShowNonHKBlock(false);
      setDeliveryEmail("");
      setDeliveryAddress("");
      setDeliveryContact("");
      setSenderWhatsapp("");
      setDeliveryDate(undefined);
      setPersonalisationName("");
      setPhoneType("");
      setKeyHolderMode("photo");
      setInscription("");
    }
  }, [open, contactName, generatedMessage]);

  // --- product-specific validity ---
  function productFieldsValid(): boolean {
    const effectiveKey = productKey === "signature_box" ? getKeepsakeKey(selectedKeepsake) : productKey;

    switch (effectiveKey) {
      case "greeting_card":
        return message.trim().length > 0;
      case "journal":
      case "desk_calendar":
        return personalisationName.trim().length > 0;
      case "mug":
        return true; // photo collected via WhatsApp
      case "phone_case":
        return phoneType.trim().length > 0;
      case "key_holder":
        return keyHolderMode === "inscription"
          ? inscription.trim().length > 0
          : true; // photo collected via WhatsApp
      default:
        return productKey === "signature_box" ? true : message.trim().length > 0;
    }
  }

  const isValid =
    recipientName.trim() &&
    senderWhatsapp.trim() &&
    deliveryDate &&
    productFieldsValid() &&
    (isDigitalOnly
      ? deliveryEmail.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(deliveryEmail.trim())
      : deliveryAddress.trim() && deliveryContact.trim() && !phoneError && !addressError && !showNonHKBlock);

  const handleAddressBlur = () => {
    const err = validateAddress(deliveryAddress);
    setAddressError(err);
    if (!err && deliveryAddress.trim() && !looksLikeHK(deliveryAddress)) {
      if (isCard) {
        setShowDigitalDialog(true);
      } else {
        setShowNonHKBlock(true);
      }
    } else {
      setShowNonHKBlock(false);
    }
  };

  const handlePhoneBlur = () => {
    if (deliveryContact.trim()) {
      setPhoneError(validatePhone(deliveryContact));
    }
  };

  const handleWhatsappBlur = () => {
    if (senderWhatsapp.trim()) {
      const err = validatePhone(senderWhatsapp);
      setWhatsappError(err);
    }
  };

  /** Build the structured personalisation_message for the order */
  function buildPersonalisationMessage(): string {
    const effectiveKey = productKey === "signature_box" ? getKeepsakeKey(selectedKeepsake) : productKey;
    const parts: string[] = [];

    switch (effectiveKey) {
      case "greeting_card":
        return message.trim();
      case "journal":
        parts.push(`Name: ${personalisationName.trim()}`);
        if (message.trim()) parts.push(`Note: ${message.trim()}`);
        return parts.join(" | ");
      case "desk_calendar":
        parts.push(`Name: ${personalisationName.trim()}`);
        if (message.trim()) parts.push(`Details: ${message.trim()}`);
        return parts.join(" | ");
      case "mug":
        parts.push("Photo: to be collected via WhatsApp");
        if (message.trim()) parts.push(`Note: ${message.trim()}`);
        return parts.join(" | ");
      case "phone_case":
        parts.push(`Phone: ${phoneType.trim()}`);
        parts.push("Photo: to be collected via WhatsApp");
        if (message.trim()) parts.push(`Note: ${message.trim()}`);
        return parts.join(" | ");
      case "key_holder":
        if (keyHolderMode === "inscription") {
          parts.push(`Inscription: ${inscription.trim()}`);
        } else {
          parts.push("Photo: to be collected via WhatsApp");
        }
        if (message.trim()) parts.push(`Note: ${message.trim()}`);
        return parts.join(" | ");
      default:
        return message.trim();
    }
  }

  const handleConfirm = async () => {
    const pErr = isDigitalOnly ? null : validatePhone(deliveryContact);
    const aErr = isDigitalOnly ? null : validateAddress(deliveryAddress);
    const wErr = validatePhone(senderWhatsapp);
    if (pErr || aErr || wErr) {
      setPhoneError(pErr);
      setAddressError(aErr);
      setWhatsappError(wErr);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    if (!isValid) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("log-gift-order", {
        body: {
          product_id: productId,
          contact_id: contactId || null,
          occasion_type: occasionType || null,
          recipient_name: recipientName.trim(),
          sender_whatsapp: senderWhatsapp.trim(),
          personalisation_message: buildPersonalisationMessage(),
          delivery_address: isDigitalOnly ? `DIGITAL — ${deliveryEmail.trim()}` : deliveryAddress.trim(),
          delivery_contact: isDigitalOnly ? deliveryEmail.trim() : deliveryContact.trim(),
          delivery_date: deliveryDate ? format(deliveryDate, "yyyy-MM-dd") : null,
          is_digital: isDigitalOnly,
          selected_keepsake_id: selectedKeepsake?.id || null,
          phone_model: phoneType.trim() || null,
          personalisation_name: personalisationName.trim() || null,
          inscription: inscription.trim() || null,
          personalisation_type: (productKey === "key_holder" || (productKey === "signature_box" && getKeepsakeKey(selectedKeepsake) === "key_holder"))
            ? keyHolderMode : null,
        },
      });

      if (error) throw error;

      const checkoutUrl = data?.checkout_url || data?.stripe_payment_link;
      toast.success("On its way 💛");
      setShowGiftSuccess(true);
      setTimeout(() => {
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        } else {
          setShowGiftSuccess(false);
          onOpenChange(false);
        }
      }, 1200);
    } catch (err) {
      console.error("Gift order error:", err);
      toast.error("Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // --- dynamic labels ---
  const modalTitle = `Send a ${productName}`;
  const subtitleText = isCard
    ? "We'll print and post it for you."
    : "We'll personalise and deliver it for you.";

  // --- keepsake-specific fields for signature box ---
  const renderKeepsakeFields = () => {
    const keepsakeKey = getKeepsakeKey(selectedKeepsake);
    switch (keepsakeKey) {
      case "journal":
        return (
          <div className="space-y-1.5">
            <Label htmlFor="personalisation-name" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Name to emboss on the cover
            </Label>
            <Input
              id="personalisation-name"
              value={personalisationName}
              onChange={(e) => setPersonalisationName(e.target.value)}
              placeholder="e.g. Sarah"
              className="rounded-xl"
              maxLength={50}
              required
            />
          </div>
        );
      case "desk_calendar":
        return (
          <div className="space-y-1.5">
            <Label htmlFor="personalisation-name" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Name to print on the calendar
            </Label>
            <Input
              id="personalisation-name"
              value={personalisationName}
              onChange={(e) => setPersonalisationName(e.target.value)}
              placeholder="e.g. Sarah's Desk"
              className="rounded-xl"
              maxLength={50}
              required
            />
          </div>
        );
      case "phone_case":
        return (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="phone-type" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Phone model
              </Label>
              <Input
                id="phone-type"
                value={phoneType}
                onChange={(e) => setPhoneType(e.target.value)}
                placeholder="e.g. iPhone 15 Pro, Samsung Galaxy S24"
                className="rounded-xl"
                maxLength={100}
                required
              />
              <WhatsAppNote />
            </div>
          </>
        );
      case "mug":
        return <WhatsAppNote />;
      case "key_holder":
        return renderKeyHolderFields();
      default:
        return null;
    }
  };

  const renderKeyHolderFields = () => (
    <>
      <div className="space-y-2">
        <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Personalisation type
        </Label>
        <RadioGroup
          value={keyHolderMode}
          onValueChange={(v) => setKeyHolderMode(v as "photo" | "inscription")}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="photo" id="kh-photo" />
            <Label htmlFor="kh-photo" className="text-sm font-normal cursor-pointer">Send a photo</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="inscription" id="kh-inscription" />
            <Label htmlFor="kh-inscription" className="text-sm font-normal cursor-pointer">Add an inscription</Label>
          </div>
        </RadioGroup>
      </div>

      {keyHolderMode === "photo" ? (
        <WhatsAppNote />
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="inscription-text" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Inscription (max {INSCRIPTION_MAX} characters)
          </Label>
          <Input
            id="inscription-text"
            value={inscription}
            onChange={(e) => setInscription(e.target.value.slice(0, INSCRIPTION_MAX))}
            placeholder="e.g. Always ✓"
            className="rounded-xl"
            maxLength={INSCRIPTION_MAX}
            required
          />
          <p className="text-right text-xs text-muted-foreground">{inscription.length} / {INSCRIPTION_MAX} characters</p>
        </div>
      )}
    </>
  );

  // --- product-specific fields ---
  const renderProductFields = () => {
    switch (productKey) {
      case "greeting_card":
        return (
          <div className="space-y-1.5">
            <Label htmlFor="card-message" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Message to print on the card
            </Label>
            <Textarea
              id="card-message"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
              className="min-h-[100px] rounded-xl"
              maxLength={MESSAGE_MAX}
              required
            />
            <p className="text-right text-xs text-muted-foreground">{message.length}/{MESSAGE_MAX}</p>
          </div>
        );

      case "signature_box":
        return (
          <>
            {renderKeepsakeFields()}
            <div className="space-y-1.5">
              <Label htmlFor="card-message" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Any special notes for personalisation (optional)
              </Label>
              <Textarea
                id="card-message"
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
                className="min-h-[80px] rounded-xl"
                maxLength={MESSAGE_MAX}
              />
              <p className="text-right text-xs text-muted-foreground">{message.length}/{MESSAGE_MAX}</p>
            </div>
          </>
        );

      case "journal":
        return (
          <div className="space-y-1.5">
            <Label htmlFor="personalisation-name" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Name to emboss on the cover
            </Label>
            <Input
              id="personalisation-name"
              value={personalisationName}
              onChange={(e) => setPersonalisationName(e.target.value)}
              placeholder="e.g. Sarah"
              className="rounded-xl"
              maxLength={50}
              required
            />
          </div>
        );

      case "desk_calendar":
        return (
          <div className="space-y-1.5">
            <Label htmlFor="personalisation-name" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Name to print on the calendar
            </Label>
            <Input
              id="personalisation-name"
              value={personalisationName}
              onChange={(e) => setPersonalisationName(e.target.value)}
              placeholder="e.g. Sarah's Desk"
              className="rounded-xl"
              maxLength={50}
              required
            />
          </div>
        );

      case "mug":
        return <WhatsAppNote />;

      case "phone_case":
        return (
          <div className="space-y-1.5">
            <Label htmlFor="phone-type" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Phone model
            </Label>
            <Input
              id="phone-type"
              value={phoneType}
              onChange={(e) => setPhoneType(e.target.value)}
              placeholder="e.g. iPhone 15 Pro, Samsung Galaxy S24"
              className="rounded-xl"
              maxLength={100}
              required
            />
            <WhatsAppNote />
          </div>
        );

      case "key_holder":
        return renderKeyHolderFields();

      default:
        return (
          <div className="space-y-1.5">
            <Label htmlFor="card-message" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Personalisation details
            </Label>
            <Textarea
              id="card-message"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
              className="min-h-[100px] rounded-xl"
              maxLength={MESSAGE_MAX}
              required
            />
            <p className="text-right text-xs text-muted-foreground">{message.length}/{MESSAGE_MAX}</p>
          </div>
        );
    }
  };

  const formContent = (
    <div className="space-y-5 px-1">
      {/* Product info */}
      <div className="flex items-center gap-3">
        {productImageUrl ? (
          <img src={productImageUrl} alt={productName} className="h-12 w-12 rounded-full object-cover" />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
            <Gift className="h-5 w-5 text-accent" />
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-foreground">{productName}</p>
          <p className="text-sm font-semibold" style={{ color: '#C9A84C' }}>{productPrice}</p>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{subtitleText}</p>

      {selectedKeepsake && (
        <div className="flex items-center gap-2 py-2 px-3 bg-amber-50 rounded-lg border border-amber-200">
          {selectedKeepsake.image_url && (
            <img
              src={selectedKeepsake.image_url}
              className="w-8 h-8 rounded-full object-cover"
              alt={selectedKeepsake.name}
            />
          )}
          <p className="text-sm text-foreground">
            Includes: <span className="font-medium">{selectedKeepsake.name}</span>
          </p>
        </div>
      )}

      {/* HK delivery notice */}
      {isCard ? (
        isDigitalOnly ? (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
            <p className="text-xs text-amber-800">
              📧 Your address appears to be outside Hong Kong. A <strong>digital copy</strong> of the card will be emailed instead.
            </p>
          </div>
        ) : (
          <div className="rounded-xl bg-accent/5 p-3">
            <p className="text-xs text-muted-foreground">
              🇭🇰 Physical delivery currently available in <strong>Hong Kong</strong> only. Other regions will receive a digital copy.
            </p>
          </div>
        )
      ) : (
        <div className="rounded-xl bg-accent/5 p-3">
          <p className="text-xs text-muted-foreground">
            🇭🇰 Physical delivery currently available in <strong>Hong Kong</strong> only.
          </p>
        </div>
      )}

      {/* Recipient name */}
      <div className="space-y-1.5">
        <Label htmlFor="recipient-name" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Recipient name</Label>
        <Input
          id="recipient-name"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          className="rounded-xl"
          maxLength={100}
          required
        />
      </div>

      {/* Product-specific fields */}
      {renderProductFields()}

      {/* Delivery address */}
      {!(isCard && isDigitalOnly) && (
        <div className="space-y-1.5">
          <Label htmlFor="delivery-address" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Delivery address</Label>
          <Textarea
            id="delivery-address"
            value={deliveryAddress}
            onChange={(e) => {
              setDeliveryAddress(e.target.value);
              if (addressError) setAddressError(null);
              if (showNonHKBlock) setShowNonHKBlock(false);
            }}
            onBlur={handleAddressBlur}
            placeholder="Full address including building, floor, district"
            className={cn("min-h-[80px] rounded-xl", addressError && "border-destructive")}
            required
          />
          {addressError && <p className="text-xs text-destructive">{addressError}</p>}
          {showNonHKBlock && !isCard && (
            <div className="rounded-xl bg-destructive/10 border border-destructive/30 p-3">
              <p className="text-xs text-destructive flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                This product can only be delivered within Hong Kong. Please enter a Hong Kong address.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Delivery email (digital — greeting card only) */}
      {isCard && isDigitalOnly && (
        <div className="space-y-1.5">
          <Label htmlFor="delivery-email" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Recipient email</Label>
          <Input
            id="delivery-email"
            type="email"
            value={deliveryEmail}
            onChange={(e) => setDeliveryEmail(e.target.value)}
            placeholder="recipient@example.com"
            className="rounded-xl"
            required
          />
          <p className="text-xs text-muted-foreground">The digital card will be sent to this email after payment.</p>
        </div>
      )}

      {/* Delivery contact (physical only) */}
      {!(isCard && isDigitalOnly) && (
        <div className="space-y-1.5">
          <Label htmlFor="delivery-contact" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Delivery contact number</Label>
          <Input
            id="delivery-contact"
            value={deliveryContact}
            onChange={(e) => {
              setDeliveryContact(e.target.value);
              if (phoneError) setPhoneError(null);
            }}
            onBlur={handlePhoneBlur}
            placeholder="+852 9123 4567"
            className={cn("rounded-xl", phoneError && "border-destructive")}
            required
          />
          {phoneError && <p className="text-xs text-destructive">{phoneError}</p>}
        </div>
      )}

      {/* Preferred delivery date */}
      {!(isCard && isDigitalOnly) && (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Preferred delivery date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start rounded-xl text-left font-normal",
                  !deliveryDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {deliveryDate ? format(deliveryDate, "PPP") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={deliveryDate}
                onSelect={setDeliveryDate}
                disabled={(date) => date < earliestDate}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
          <p className="text-xs text-muted-foreground">Earliest available delivery is 7 days from today</p>
        </div>
      )}

      {/* Your WhatsApp number */}
      <div className="space-y-1.5">
        <Label htmlFor="sender-whatsapp" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Your WhatsApp number</Label>
        <Input
          id="sender-whatsapp"
          value={senderWhatsapp}
          onChange={(e) => {
            setSenderWhatsapp(e.target.value);
            if (whatsappError) setWhatsappError(null);
          }}
          onBlur={handleWhatsappBlur}
          placeholder="+852 9123 4567"
          className={cn("rounded-xl", whatsappError && "border-destructive")}
          required
        />
        {whatsappError && <p className="text-xs text-destructive">{whatsappError}</p>}
        <p className="text-xs text-muted-foreground">
          We'll message you here to confirm personalisation details before we start production.
        </p>
      </div>

      {/* Occasion */}
      {occasionType && (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Occasion</Label>
          <p className="text-sm text-foreground capitalize">{occasionType.replace(/_/g, " ")}</p>
        </div>
      )}

      {/* CTA */}
      <Button
        onClick={handleConfirm}
        disabled={submitting || !isValid}
        className="w-full rounded-full"
        size="lg"
        style={{ backgroundColor: '#1A1A1A' }}
      >
        {submitting ? "Processing…" : `Confirm & Pay — ${productPrice} →`}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        {isCard && isDigitalOnly
          ? "You'll be taken to a secure payment page. Your digital card will be emailed after payment."
          : "You'll be taken to a secure payment page. Your gift will be personalised and delivered after payment."}
      </p>

      <button
        onClick={() => onOpenChange(false)}
        className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
      >
        Cancel
      </button>

      {/* Non-HK address dialog (greeting card only) */}
      {isCard && (
        <AlertDialog open={showDigitalDialog} onOpenChange={setShowDigitalDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Delivery outside Hong Kong
              </AlertDialogTitle>
              <AlertDialogDescription>
                Physical card delivery is currently available in Hong Kong only. We can send a beautifully designed <strong>digital copy</strong> of your personalised card via email instead.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setShowDigitalDialog(false);
                setDeliveryAddress("");
              }}>
                Edit address
              </AlertDialogCancel>
              <AlertDialogAction onClick={() => {
                setIsDigitalOnly(true);
                setShowDigitalDialog(false);
              }}>
                Send digital copy
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );

  const successOverlay = (
    <AnimatePresence>
      {showGiftSuccess && (
        <motion.div
          initial={reduce ? { opacity: 1 } : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-background px-6 text-center"
        >
          <motion.div
            initial={reduce ? false : { scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="mb-6 flex h-20 w-20 items-center justify-center rounded-full"
            style={{ backgroundColor: "#C9A84C" }}
          >
            <Check className="h-10 w-10 text-white" strokeWidth={3} />
          </motion.div>
          <p className="mb-2 font-serif text-2xl font-semibold text-foreground">On its way.</p>
          <p className="text-body text-muted-foreground">
            {recipientName || "They"} {recipientName ? "is" : "are"} lucky to have you.
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (isMobile) {
    return (
      <>
        {successOverlay}
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent className="max-h-[90vh]">
            <DrawerHeader className="text-left px-4">
              <DrawerTitle className="font-serif text-xl">{modalTitle}</DrawerTitle>
            </DrawerHeader>
            <div className="overflow-y-auto flex-1 px-4 pb-6">
              {formContent}
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <>
      {successOverlay}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{modalTitle}</DialogTitle>
          </DialogHeader>
          {formContent}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GiftPersonalisationModal;
